import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Check, Copy } from 'lucide-react';
import {
  Button, Card, CardContent, CardHeader, CardTitle, Spinner,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui';
import { ApiErrorDisplay } from '@/components/features/error';
import { TransactionStatusBadge } from '@/components/features/transactions/status-badge';
import { useAdminTransaction, useTransactionDetail } from '@/hooks/use-admin-transactions';
import { useMerchantScope } from '@/hooks/use-merchant-scope';
import { TransactionTimeline } from '@/components/timeline/TransactionTimeline';
import { TransactionTraceView } from '@/components/trace/TransactionTraceView';
import { formatDate, formatCents, truncate } from '@/lib/utils';

export function TransactionDetailPage() {
  const { t } = useTranslation('transactions');
  const { transactionId } = useParams<{ transactionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Spec 0220: Merchant-Feld nur fuer Platform-Rollen.
  // Spec 143: showMerchant == isPlatformRole(user.role) — dieselbe Ableitung
  // gated auch den Trace-Tab (rawLog ist nicht tenant-sicher, Spec 143 §6).
  const { showMerchant } = useMerchantScope();
  // Spec 0027: merchantId from route-state (passed by transaction-table navigate)
  // Issue #300: `from` traegt die Listen-Query (location.search) zurueck, damit
  // der Zurueck-Button die gesetzten Filter wiederherstellt. Fallback: nackte Liste.
  const navState = location.state as { merchantId?: string; from?: string } | null;
  const merchantId = navState?.merchantId;
  const backTo = `/transactions${navState?.from ?? ''}`;
  // Spec 143: Trace-Tab lazy — der Trace-Request feuert erst beim Tab-Wechsel
  const [activeTab, setActiveTab] = useState('overview');
  // Spec 475: Copy-Affordanz fuer die IQ-ID (Muster transaction-status.tsx)
  const [iqIdCopied, setIqIdCopied] = useState(false);

  const handleCopyIqId = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setIqIdCopied(true);
      setTimeout(() => setIqIdCopied(false), 2000);
    } catch (err) {
      if (import.meta.env.DEV) console.error('Failed to copy IQ transaction id:', err);
    }
  };

  const { data: transaction, isLoading, error, refetch } = useAdminTransaction(transactionId, merchantId);
  const { data: detailData, isLoading: isTimelineLoading, error: timelineError } = useTransactionDetail(transactionId);

  if (!transactionId) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Missing transaction ID</p>
        <Button variant="link" onClick={() => navigate(backTo)}>
          {t('detail.back')}
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ApiErrorDisplay error={error} onRetry={() => refetch()} />;
  }

  if (!transaction) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Transaction not found</p>
        <Button variant="link" onClick={() => navigate(backTo)}>
          {t('detail.back')}
        </Button>
      </div>
    );
  }

  const sortedWebhooks = [...(transaction.webhookHistory || [])].sort((a, b) => {
    if (a.step != null && b.step != null) return a.step.localeCompare(b.step);
    return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
  });

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t('detail.title')}
          </h1>
          <p className="mt-1 font-mono text-sm text-muted-foreground">
            {truncate(transaction.transactionId, 24)}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate(backTo)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('detail.back')}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Tab-Leiste nur zeigen, wenn es mehr als einen Tab gibt (Spec 143 §6:
            Merchant-Rollen haben keinen Trace-Tab — dann auch keine Leiste) */}
        {showMerchant && (
          <TabsList className="mb-4">
            <TabsTrigger value="overview">{t('trace.tabOverview')}</TabsTrigger>
            <TabsTrigger value="trace">{t('trace.tabTrace')}</TabsTrigger>
          </TabsList>
        )}

        <TabsContent value="overview">
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('detail.basicInfo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.transactionId')}</p>
              <p className="font-mono">{transaction.transactionId}</p>
            </div>
            {/* Spec 475 (v2.17): sparse — Zeilen erscheinen nur, wenn die Facade
                die Felder liefert (fehlend heisst fehlend, kein null). */}
            {transaction.internetqTransactionId && (
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.internetqTransactionId')}</p>
                <div className="flex items-center gap-1">
                  <p className="break-all font-mono">{transaction.internetqTransactionId}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => handleCopyIqId(transaction.internetqTransactionId!)}
                    aria-label={t('detail.copyIqId')}
                  >
                    {iqIdCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
            )}
            {transaction.customerReference && (
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.customerReference')}</p>
                <p className="break-all font-mono">{transaction.customerReference}</p>
              </div>
            )}
            {showMerchant && (
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.merchant')}</p>
                <p className="font-mono">{transaction.merchantId}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.service')}</p>
              <p className="font-mono">{transaction.serviceId}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.status')}</p>
              <TransactionStatusBadge status={transaction.status} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.flow')}</p>
              <p>{transaction.flowType}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.amount')}</p>
              <p>{transaction.grossAmount != null ? formatCents(transaction.grossAmount) : '-'}</p>
            </div>
            {transaction.destination && (
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.msisdn')}</p>
                <p className="font-mono">{transaction.destination}</p>
              </div>
            )}
            {transaction.operatorId && (
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.operator')}</p>
                <p>{transaction.operatorId}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.created')}</p>
              <p>{formatDate(transaction.createdAt)}</p>
            </div>
            {transaction.lastUpdate && (
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.lastUpdate')}</p>
                <p>{formatDate(transaction.lastUpdate)}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('detail.webhookHistory')}</CardTitle>
          </CardHeader>
          <CardContent>
            {sortedWebhooks.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {t('detail.noWebhooks')}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('detail.webhookColumns.step')}</TableHead>
                    <TableHead>{t('detail.webhookColumns.type')}</TableHead>
                    <TableHead>{t('detail.webhookColumns.statusCode')}</TableHead>
                    <TableHead>{t('detail.webhookColumns.timestamp')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedWebhooks.map((wh, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{wh.step ?? '-'}</TableCell>
                      <TableCell>{wh.eventType}</TableCell>
                      <TableCell>{wh.statusCode ?? '-'}</TableCell>
                      <TableCell>{formatDate(wh.timestamp)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Timeline Section (Spec 141) */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-base">Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          {timelineError ? (
            <p className="py-4 text-center text-sm text-destructive">
              {t('detail.timelineError')}
            </p>
          ) : (
            <TransactionTimeline
              timeline={detailData?.timeline ?? []}
              isLoading={isTimelineLoading}
            />
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Trace-Tab (Spec 143): Platform-Rollen only; Inhalt wird erst beim
            Aktivieren gemountet — der Mount ist das Lazy-Gate fuer den Fetch */}
        {showMerchant && (
          <TabsContent value="trace">
            {activeTab === 'trace' && (
              <TransactionTraceView transactionId={transactionId} />
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
